import { useEffect, useState } from "preact/hooks";
import { t } from "../i18n";
import type { StaticEvidenceProvider } from "../data/provider";
import {
  authEnabled,
  getSession,
  listResults,
  loginAccount,
  logoutAccount,
  registerAccount,
  subscribeAuth,
  type AuthSession,
  type SavedResultSummary,
} from "../app/auth";
import { GoogleContinue } from "../components/GoogleContinue";
import { PrivacyNotice } from "../components/PrivacyNotice";

export function MyResultsPage({ provider }: { provider: StaticEvidenceProvider | null }) {
  const [session, setSession] = useState<AuthSession>(getSession);
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState<SavedResultSummary[] | null>(null);

  useEffect(() => subscribeAuth(setSession), []);

  useEffect(() => {
    if (!session.email || !authEnabled()) {
      setRows(null);
      return;
    }
    let gone = false;
    listResults()
      .then((list) => {
        if (!gone) setRows(list);
      })
      .catch((err: unknown) => {
        if (!gone) setError(err instanceof Error ? err.message : t("account.error"));
      });
    return () => {
      gone = true;
    };
  }, [session.email]);

  const submit = async () => {
    setError("");
    if (mode === "register" && !consent) {
      setError(t("account.consent.required"));
      return;
    }
    setBusy(true);
    try {
      if (mode === "register") {
        await registerAccount(email.trim(), password, consent);
      } else {
        await loginAccount(email.trim(), password);
      }
      setPassword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("account.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article class="obs-page obs-account-page">
      <p class="obs-eyebrow">{t("brand.observatory")}</p>
      <h1>{t("account.myResults")}</h1>
      <p class="obs-lede">{t("account.myResults.body")}</p>
      <PrivacyNotice />

      {!authEnabled() && <p class="obs-notice">{t("account.unavailable")}</p>}

      {authEnabled() && !session.email && (
        <form
          class="obs-auth-form obs-auth-card"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div class="obs-auth-modes" role="tablist">
            <button type="button" class={mode === "login" ? "is-selected" : undefined} onClick={() => setMode("login")}>
              {t("account.login")}
            </button>
            <button
              type="button"
              class={mode === "register" ? "is-selected" : undefined}
              onClick={() => setMode("register")}
            >
              {t("account.create")}
            </button>
          </div>
          {mode === "register" && (
            <label class="obs-consent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent((e.target as HTMLInputElement).checked)} />
              <span>
                {t("account.consent")}{" "}
                <a href={session.privacyUrl} target="_blank" rel="noreferrer">
                  {t("account.privacyLink")}
                </a>
                .
              </span>
            </label>
          )}
          <GoogleContinue
            consent={consent}
            requireConsent={mode === "register"}
            disabled={busy}
            onError={setError}
          />
          <label>
            <span>{t("account.email")}</span>
            <input
              type="email"
              autocomplete="username"
              required
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            />
          </label>
          <label>
            <span>{t("account.password")}</span>
            <input
              type="password"
              autocomplete={mode === "register" ? "new-password" : "current-password"}
              minLength={8}
              required
              value={password}
              onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
            />
          </label>
          {error ? <p class="obs-form-error" role="alert">{error}</p> : null}
          <p class="obs-wizard-nav">
            <button class="obs-btn obs-btn-primary" type="submit" disabled={busy}>
              {mode === "register" ? t("account.create") : t("account.login")}
            </button>
          </p>
        </form>
      )}

      {session.email && (
        <>
          <p class="obs-account-who">
            {t("account.signedIn", { email: session.email })}{" "}
            <button
              class="obs-btn obs-btn-text"
              type="button"
              onClick={() => {
                void logoutAccount();
              }}
            >
              {t("account.logout")}
            </button>
          </p>
          {error ? <p class="obs-form-error" role="alert">{error}</p> : null}
          {rows && rows.length === 0 && <p class="obs-empty">{t("account.empty")}</p>}
          {rows && rows.length > 0 && (
            <ul class="obs-result-list">
              {rows.map((row) => {
                const title = provider?.careerById(row.careerId)?.title ?? row.careerId;
                const when = new Date(row.createdAt.includes("T") ? row.createdAt : `${row.createdAt}Z`);
                return (
                  <li key={row.id}>
                    <article class="obs-priority">
                      <h2>{title}</h2>
                      <p>
                        {t("match.results.score")}: {Math.round(row.matchPercent)}% ·{" "}
                        {Number.isNaN(when.getTime())
                          ? row.createdAt
                          : when.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                      </p>
                      <p>
                        <a class="obs-btn obs-btn-primary" href={`${row.printUrl}?autoprint=1`} target="_blank" rel="noreferrer">
                          {t("account.downloadPdf")}
                        </a>
                      </p>
                    </article>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </article>
  );
}
